import React, { FC } from 'react';

import { graphql } from 'gatsby';
import { Box, Text, Link, Image } from '@lucid/components-react';
import ReactHtmlParser from 'react-html-parser';
import { getProcessedValue } from '@lucid/utils';
import { Drupal_ParagraphFlexiCustomerLogos } from 'generated/drupal-graphql';
import { LucidTheme, mqMin, theme } from '@lucid/theme';

import FlourishedHeading from 'components/flourished-heading/flourished-heading';

interface FlexiCustomerLogosProps {
  entity: Drupal_ParagraphFlexiCustomerLogos;
}

type HeaderJustify = 'left' | 'center' | 'right';

const FlexiCustomerLogos: FC<FlexiCustomerLogosProps> = ({
  entity: {
    fieldCustomerLogosHeader,
    fieldFlourishedHeading,
    fieldCustomerLogosLink,
    fieldFlexiCustomerLogos,
    fieldFlexiHeaderJustify,
    fieldHasThreeAcross
  }
}) => {
  const headerJustify = fieldFlexiHeaderJustify?.entity?.entityLabel?.toLowerCase() as HeaderJustify;
  return (
    <Box
      css={(theme: LucidTheme) => ({
        label: 'flexiCustomerLogos',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        width: '100%',
        marginTop: !fieldFlourishedHeading ? 0 : theme.space[6],
        marginBottom: theme.space[10]
      })}
      gridColumn="1 / -1"
      px={{ default: 0, tablet: 6 }}
    >
      {fieldCustomerLogosHeader && fieldFlourishedHeading && (
        <Box>
          <FlourishedHeading
            as="h2"
            css={(theme: LucidTheme) => ({
              marginBottom: theme.space[3],
              [mqMin.tabletLandscape]: {
                flex: '0 1 45%',
                maxWidth: '45%'
              }
            })}
            variant="headline3XL"
          >
            {ReactHtmlParser(getProcessedValue(fieldCustomerLogosHeader))}
          </FlourishedHeading>
        </Box>
      )}
      {fieldCustomerLogosHeader && !fieldFlourishedHeading && (
        <Text
          as="div"
          css={{
            textAlign: headerJustify,
            '& > p': {
              marginTop: 0
            }
          }}
          variant="paragraphL300"
        >
          {ReactHtmlParser(getProcessedValue(fieldCustomerLogosHeader))}
        </Text>
      )}
      {fieldFlexiCustomerLogos && (
        <Box
          css={(theme: LucidTheme) => [
            {
              label: 'content',
              display: 'flex',
              flexWrap: 'nowrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: theme.space[6],
              paddingRight: theme.space[6],
              marginBottom: theme.space[4],
              width: '100%',
              '&:after': {
                content: '""',
                flex: '1 0 auto'
              },
              [mqMin.tabletSmall]: {
                flexWrap: 'wrap',
              }
            },
            fieldHasThreeAcross && {
              justifyContent: 'center'
            }
          ]}
          flexDirection={['column', 'column', 'column', 'row', 'row']}
        >
          {fieldFlexiCustomerLogos?.map(
            ({ entity: { uuid, fieldImageLink, fieldAltText } }: any, index) => (
              <Box
                key={uuid || `customerLogo-${index}`}
                css={[
                  {
                    label: 'image',
                    flex: '0 1 33%',
                    maxWidth: '170px',
                    [mqMin.tabletLandscape]: {
                      flex: '0 1 20%',
                    }
                  },
                  fieldHasThreeAcross && {
                    flex: '0 1 33%',
                    maxWidth: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    [mqMin.tabletSmall]: {
                      maxWidth: '33% !important',
                      paddingTop: '0 !important',
                      paddingRight: '0 !important',
                      paddingBottom: '0 !important',
                      paddingLeft: '0 !important'
                    }
                  }
                ]}
                marginY={[
                  theme.space[4]
                ]}
                paddingX={[
                  0,
                  0,
                  0,
                  theme.space[1],
                  theme.space[3],
                  theme.space[4]
                ]}
                paddingY={[
                  theme.space[1],
                  theme.space[1],
                  theme.space[1],
                  0,
                  0
                ]}
              >
                <Image
                  css={{
                    maxHeight: fieldHasThreeAcross ? '60px' : '100%'
                  }}
                  imageAlt={fieldAltText}
                  imageSrc={fieldImageLink}
                  loading="lazy"
                />
              </Box>
            )
          )}
        </Box>
      )}
      {fieldCustomerLogosLink && (
        <Box>
          <Text
            as="div"
            css={{
              display: 'flex',
              justifyContent: 'center',
              [mqMin.tabletLandscape]: {
                justifyContent: 'flex-end'
              }
            }}
            variant="interactiveS400"
          >
            <Link hasArrow to={fieldCustomerLogosLink?.url?.path || ''}>
              {fieldCustomerLogosLink?.title}
            </Link>
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default FlexiCustomerLogos;

export const query = graphql`
  fragment flexiCustomerLogosSection on Drupal_ParagraphFlexiCustomerLogos {
    fieldFlourishedHeading
    fieldHasThreeAcross
    fieldCustomerLogosHeader {
      processed
    }
    fieldFlexiCustomerLogos {
      entity {
        ... on Drupal_ParagraphSeoImage {
          uuid
          fieldImageLink
          fieldAltText
        }
      }
    }
    fieldCustomerLogosLink {
      title
      url {
        path
      }
    }
    fieldFlexiHeaderJustify {
      entity {
        entityLabel
      }
    }
  }
`;
